import { useEffect, useState, FormEvent } from 'react';
import { Modal } from './Modal';
import {
  sendDocumentEmail,
  parseEmailList,
  type DocumentType,
} from '../lib/docEmail';

interface Props {
  open: boolean;
  onClose: () => void;

  /** Document title — shown at the top of the modal so the user can confirm what they're sending. */
  docTitle: string;
  /** Drives the email's subject line and body copy. */
  docType: DocumentType;

  /**
   * Lazy share-URL resolver. Called the moment the user clicks Send.
   * Lazy so we don't burn a signed URL when the modal just opens.
   * Should return an absolute http(s) URL.
   */
  getShareUrl: () => Promise<string>;

  /** Optional context fed into the email body for clarity. */
  orgName?: string;
  siteName?: string;
  senderName?: string;
  /** Pre-fill the recipient field (e.g. with a subbie's email). */
  defaultRecipients?: string[];

  /** Copy for the "send after creating a document" auto-open variant. */
  introCopy?: string;
}

export function ShareModal({
  open,
  onClose,
  docTitle,
  docType,
  getShareUrl,
  orgName,
  siteName,
  senderName,
  defaultRecipients,
  introCopy,
}: Props) {
  const [recipients, setRecipients] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Reset state every time the modal is reopened.
  useEffect(() => {
    if (open) {
      setRecipients((defaultRecipients ?? []).join(', '));
      setNote('');
      setResult(null);
      setSending(false);
    }
  }, [open, defaultRecipients]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const to = parseEmailList(recipients);
    if (to.length === 0) {
      setResult({ ok: false, msg: 'Add at least one email address.' });
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const docUrl = await getShareUrl();
      const r = await sendDocumentEmail({
        to,
        docTitle,
        docType,
        docUrl,
        orgName,
        siteName,
        senderName,
        note: note.trim() || undefined,
      });
      if (r.sent) {
        setResult({
          ok: true,
          msg: `Sent to ${r.recipients ?? to.length} ${(r.recipients ?? to.length) === 1 ? 'address' : 'addresses'}.`,
        });
      } else {
        setResult({ ok: false, msg: r.error || 'Send failed.' });
      }
    } catch (err) {
      setResult({ ok: false, msg: err instanceof Error ? err.message : String(err) });
    } finally {
      setSending(false);
    }
  }

  // Fallback for when the Edge Function fails — open the user's native email
  // app with the same content pre-filled, so they can still hand it off.
  async function onMailtoFallback() {
    try {
      const to = parseEmailList(recipients);
      const docUrl = await getShareUrl();
      const subject = encodeURIComponent(`${docType} — ${docTitle}${orgName ? ` (${orgName})` : ''}`);
      const body = encodeURIComponent(
        `Please find a copy of "${docTitle}" via the link below — valid for 30 days.\n\n` +
        `${docUrl}\n\n` +
        (siteName ? `Site: ${siteName}\n` : '') +
        (orgName ? `Issued by ${orgName}.\n\n` : '\n') +
        (note.trim() ? `${note.trim()}\n\n` : '') +
        `Sent from Safe and Sorted.`,
      );
      window.location.href = `mailto:${to.join(',')}?subject=${subject}&body=${body}`;
    } catch (e) {
      setResult({ ok: false, msg: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <Modal open={open} title={`Email this ${docType}`} onClose={onClose}>
      <form onSubmit={onSubmit}>
        {introCopy && (
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 14, lineHeight: 1.4 }}>
            {introCopy}
          </p>
        )}

        <div className="card" style={{ marginBottom: 12, background: 'var(--brand-pale)' }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-soft)' }}>Sending:</p>
          <p style={{ margin: '2px 0 0', fontWeight: 600 }}>{docTitle}</p>
        </div>

        <div className="field">
          <label htmlFor="share-to">Send to</label>
          <input
            id="share-to"
            type="text"
            autoFocus
            inputMode="email"
            autoComplete="email"
            enterKeyHint="next"
            placeholder="someone@example.com, another@example.com"
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            disabled={sending}
          />
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
            Separate multiple addresses with commas. Up to 10 at a time.
          </p>
        </div>

        <div className="field">
          <label htmlFor="share-note">Add a message (optional)</label>
          <textarea
            id="share-note"
            rows={3}
            placeholder="e.g. Here's the RAMS for tomorrow's lift. Please sign and return."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={sending}
          />
        </div>

        {result && (
          <div className={`banner ${result.ok ? 'banner-success' : 'banner-error'}`} style={{ marginTop: 4 }}>
            {result.msg}
          </div>
        )}

        <div className="btn-stack" style={{ marginTop: 14 }}>
          {result?.ok ? (
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Done
            </button>
          ) : (
            <>
              <button type="submit" className="btn btn-primary" disabled={sending}>
                {sending ? 'Sending…' : '📨 Send email'}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onMailtoFallback}
                disabled={sending}
                title="Open your phone's email app with the link pre-filled instead"
              >
                Open in my email app
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onClose}
                disabled={sending}
              >
                {introCopy ? 'Skip — don\'t send' : 'Cancel'}
              </button>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
}
